/*
 * App-wide "settings changed" bus. The settings and onboarding tools report
 * what they changed (`invalidate: ["profile", "law_firm", ...]`) from any
 * chat, and an undo of a settings change lands here too: the onboarding
 * progress refetches, the signed-in lawyer (name, avatar, firm) reloads, and
 * any open settings section can refetch through `useOnSettingsInvalidated`.
 */
import { useEffect, useRef } from "react";

import { SessionAPI } from "../../api/sessionApi";
import { lawyerStateFromSession } from "../../hooks/useGetLawyerData";
import { setLawyerData } from "../../redux/lawyer/lawyerSlice";
import { store } from "../../redux/store";
import { invalidateOnboarding } from "./hooks/useOnboarding";

/** Targets the settings tools report (`caseblink/case_agent/tools/settings.py`). */
export const SETTINGS_TARGETS = [
  "onboarding",
  "profile",
  "session",
  "notification_preferences",
  "law_firm",
  "agents_md",
  "time_settings",
  "time_firm_settings",
  "firm_permissions",
  "channels"
] as const;

export type SettingsTarget = (typeof SETTINGS_TARGETS)[number];

const SETTINGS = new Set<string>(SETTINGS_TARGETS);
/** What the signed-in lawyer's session carries (name, photo, firm). */
const SESSION = new Set<string>(["profile", "session", "law_firm"]);

type Listener = (targets: SettingsTarget[]) => void;
const listeners = new Set<Listener>();

let sessionReload: Promise<void> | null = null;

const reloadSession = (): Promise<void> => {
  if (sessionReload) return sessionReload;
  sessionReload = SessionAPI.getMe()
    .then((session) => {
      const next = lawyerStateFromSession(session);
      if (next && next.uid === store.getState().lawyer.uid)
        store.dispatch(setLawyerData(next));
    })
    .catch(() => undefined)
    .finally(() => {
      sessionReload = null;
    });
  return sessionReload;
};

/** Forward a `data-invalidate` list; only the settings targets matter here. */
export const notifySettingsInvalidated = (targets: readonly string[]): void => {
  const hit = targets.filter((t): t is SettingsTarget => SETTINGS.has(t));
  if (!hit.length) return;
  void invalidateOnboarding();
  if (hit.some((t) => SESSION.has(t))) void reloadSession();
  for (const fn of Array.from(listeners)) fn(hit);
};

export const subscribeSettingsInvalidated = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Run `onChange` when one of `targets` changed elsewhere (latest callback wins). */
export const useOnSettingsInvalidated = (
  targets: readonly SettingsTarget[],
  onChange: () => void
): void => {
  const ref = useRef(onChange);
  ref.current = onChange;
  const key = targets.join(",");
  useEffect(() => {
    const wanted = new Set(key.split(","));
    return subscribeSettingsInvalidated((hit) => {
      if (hit.some((t) => wanted.has(t))) ref.current();
    });
  }, [key]);
};
